import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getDevice, syncDeviceTime, type Device } from '@/api/devices'
import { createRemoteCommand, waitForCommand, type RemoteOperation } from '@/api/remote-commands'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { ArrowLeft, DoorOpen, Lock, ShieldOff, Shield, ChevronDown, Monitor, Clock } from 'lucide-react'
import { DeviceEventsTab } from './tabs/DeviceEventsTab'
import { DeviceUsersTab } from './tabs/DeviceUsersTab'
import { cn } from '@/lib/utils'

// ── Types ─────────────────────────────────────────────────────────────────────

type DoorCmd = 'unlock' | 'lock' | 'always-open' | 'always-close'

interface DoorStatusInfo {
  doorNo: number
  lockStatus: 'locked' | 'unlocked'
  doorStatus?: 'open' | 'close'
  isForceOpen?: boolean
}

// ── Status indicator ──────────────────────────────────────────────────────────

const STATUS_DOT: Record<string, string> = {
  online:  'bg-emerald-500',
  offline: 'bg-slate-400',
  locked:  'bg-amber-500',
}

const STATUS_LABEL: Record<string, string> = {
  online:  'En línea',
  offline: 'Sin conexión',
  locked:  'Bloqueado',
}

function DeviceStatusIndicator({ status }: { status: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${STATUS_DOT[status] ?? 'bg-slate-400'}`} />
      <span className="text-sm text-text-muted">{STATUS_LABEL[status] ?? status}</span>
    </div>
  )
}

// ── Capability label map ──────────────────────────────────────────────────────

const CAPABILITY_LABELS: Record<string, string> = {
  isSupportFaceRecognition:   'Reconocimiento facial',
  isSupportCard:              'Tarjeta RFID',
  isSupportFP:                'Huella digital',
  isSupportNFC:               'NFC',
  isSupportWifi:              'Wi-Fi',
  isSupportBT:                'Bluetooth',
  isSupportRS485:             'RS-485',
  isSupportTamperAlarm:       'Alarma de sabotaje',
  isSupportRemoteConfig:      'Configuración remota',
  isSupportIPv6:              'IPv6',
  isSupportDisplayId:         'Pantalla',
  isSupportQRCode:            'Código QR',
  isSupportMask:              'Detección de mascarilla',
  isSupportTemperatureDetect: 'Medición de temperatura',
  isSupportLiveness:          'Anti-spoofing',
}

function capabilityLabel(key: string): string {
  return CAPABILITY_LABELS[key] ?? key.replace(/^isSupport/, '').replace(/([A-Z])/g, ' $1').trim()
}

// ── Door actions config ───────────────────────────────────────────────────────

const DOOR_ACTIONS: { cmd: DoorCmd; label: string; icon: React.ReactNode; cls: string }[] = [
  { cmd: 'unlock',       label: 'Abrir',           icon: <DoorOpen className="w-4 h-4" />,  cls: 'bg-emerald-600 hover:bg-emerald-500 text-white' },
  { cmd: 'lock',         label: 'Cerrar',           icon: <Lock className="w-4 h-4" />,      cls: 'bg-surface border border-border text-text-primary hover:bg-border/50' },
  { cmd: 'always-open',  label: 'Siempre abierta',  icon: <ShieldOff className="w-4 h-4" />, cls: 'border border-amber-500/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10' },
  { cmd: 'always-close', label: 'Siempre cerrada',  icon: <Shield className="w-4 h-4" />,   cls: 'border border-border text-text-muted hover:text-text-primary hover:bg-border/30' },
]

// ── Main component ────────────────────────────────────────────────────────────

export function DeviceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { proxyUrl, proxyToken, isElectron } = useProxyConfig()

  const [device, setDevice] = useState<Device | null>(null)
  const [capabilities, setCapabilities] = useState<Record<string, boolean> | null>(null)
  const [doorStatus, setDoorStatus] = useState<DoorStatusInfo | null>(null)
  const [loadingDevice, setLoadingDevice] = useState(true)
  const [doorBusy, setDoorBusy] = useState(false)
  const [doorSuccess, setDoorSuccess] = useState(false)
  const [remoteBusy, setRemoteBusy] = useState(false)
  const [activeTab, setActiveTab] = useState<'events' | 'users'>('events')
  const [techOpen, setTechOpen] = useState(false)
  const [syncingTime, setSyncingTime] = useState(false)
  const doorStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const doorSuccessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!id) return
    getDevice(id)
      .then((r) => setDevice(r.device))
      .catch((e) => toast.error(e instanceof ApiError ? e.message : 'Error al cargar dispositivo'))
      .finally(() => setLoadingDevice(false))
  }, [id])

  useEffect(() => {
    if (!id || !proxyUrl || !proxyToken) return
    fetch(`${proxyUrl}/capabilities/${id}`, {
      headers: { Authorization: `Bearer ${proxyToken}` },
    })
      .then((r) => r.json())
      .then((data) => setCapabilities(data.capabilities))
      .catch(() => {})
  }, [id, proxyUrl, proxyToken])

  const fetchDoorStatus = useCallback(() => {
    if (!id || !proxyUrl || !proxyToken) return
    fetch(`${proxyUrl}/door/${id}/status`, {
      headers: { Authorization: `Bearer ${proxyToken}` },
    })
      .then((r) => r.ok ? r.json() as Promise<DoorStatusInfo[]> : Promise.reject())
      .then((statuses) => { if (statuses.length > 0) setDoorStatus(statuses[0]) })
      .catch(() => {})
  }, [id, proxyUrl, proxyToken])

  useEffect(() => {
    fetchDoorStatus()
  }, [fetchDoorStatus])

  function triggerDoorSuccess() {
    setDoorSuccess(true)
    if (doorSuccessTimerRef.current) clearTimeout(doorSuccessTimerRef.current)
    doorSuccessTimerRef.current = setTimeout(() => setDoorSuccess(false), 700)
  }

  const sendDoorCommand = useCallback(async (cmd: DoorCmd) => {
    if (!proxyUrl || !proxyToken || !id) return
    setDoorBusy(true)
    try {
      const res = await fetch(`${proxyUrl}/door/${id}/${cmd}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${proxyToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ doorNo: 1 }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Error' }))
        throw new Error((err as { error: string }).error)
      }
      const labels: Record<DoorCmd, string> = {
        'unlock':       'Puerta abierta',
        'lock':         'Puerta cerrada',
        'always-open':  'Modo siempre abierta activado',
        'always-close': 'Modo siempre cerrada activado',
      }
      toast.success(labels[cmd])
      if (cmd === 'unlock') triggerDoorSuccess()
      if (doorStatusTimerRef.current) clearTimeout(doorStatusTimerRef.current)
      doorStatusTimerRef.current = setTimeout(fetchDoorStatus, 1500)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al enviar comando')
    } finally {
      setDoorBusy(false)
    }
  }, [proxyUrl, proxyToken, id, fetchDoorStatus])

  const sendRemoteCommand = useCallback(async (operation: RemoteOperation) => {
    if (!id) return
    setRemoteBusy(true)
    try {
      const { command } = await createRemoteCommand(id, operation)
      const result = await waitForCommand(command.id)
      if (result.status === 'failed') throw new Error(result.error ?? 'Comando fallido')
      const labels: Partial<Record<RemoteOperation, string>> = {
        OPEN_DOOR:  'Puerta abierta',
        CLOSE_DOOR: 'Puerta cerrada',
      }
      toast.success(labels[operation] ?? 'Comando ejecutado')
      if (operation === 'OPEN_DOOR') triggerDoorSuccess()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al enviar comando')
    } finally {
      setRemoteBusy(false)
    }
  }, [id])

  const handleSyncTime = useCallback(async () => {
    if (!id) return
    setSyncingTime(true)
    try {
      await syncDeviceTime(id)
      toast.success('Reloj del dispositivo sincronizado')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error al sincronizar la hora')
    } finally {
      setSyncingTime(false)
    }
  }, [id])

  if (loadingDevice) return (
    <div className="flex flex-col gap-5 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-4 h-4 bg-border/50 rounded" />
        <div className="h-6 w-40 bg-border/50 rounded" />
      </div>
      <div className="h-32 bg-border/30 rounded-2xl" />
    </div>
  )
  if (!device) return <p className="text-text-muted text-sm p-4">Dispositivo no encontrado</p>

  const isOffline = device.status === 'offline'
  const isLocked = device.status === 'locked'
  const canControl = device.status === 'online'

  return (
    <div className="flex flex-col gap-5">

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/devices')}
          className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-border/50 transition-colors"
          aria-label="Volver"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
          <Monitor className="w-5 h-5 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-text-primary font-bold text-xl truncate leading-tight">{device.name}</h1>
          <DeviceStatusIndicator status={device.status} />
        </div>
        <button
          onClick={handleSyncTime}
          disabled={syncingTime || !canControl}
          title="Sincroniza el reloj del dispositivo con la hora correcta"
          className="flex items-center gap-1.5 text-xs font-medium text-text-muted border border-border px-2.5 py-1.5 rounded-lg hover:text-text-primary hover:bg-border/30 disabled:opacity-50 transition-colors cursor-pointer"
        >
          <Clock className="w-3.5 h-3.5" />
          {syncingTime ? 'Sincronizando...' : 'Sincronizar hora'}
        </button>
        {doorStatus && (
          <span className={cn(
            'text-xs font-medium px-2.5 py-1 rounded-full border',
            doorStatus.lockStatus === 'unlocked'
              ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
              : 'bg-slate-50 dark:bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/30'
          )}>
            {doorStatus.lockStatus === 'unlocked' ? 'Hoja abierta' : 'Hoja cerrada'}
          </span>
        )}
      </div>

      {/* ── Door control ─────────────────────────────────────────────────── */}
      <div className="bg-surface border border-border rounded-2xl p-5 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-text-primary font-semibold">Control de acceso</h2>
          <div className={cn(
            'w-2 h-2 rounded-full',
            canControl ? 'bg-emerald-500' : isLocked ? 'bg-amber-500' : 'bg-slate-400'
          )} />
        </div>

        {isOffline && (
          <div className="bg-slate-50 dark:bg-slate-500/10 border border-slate-200 dark:border-slate-500/20 rounded-xl p-3 text-sm text-text-muted">
            Este dispositivo no está disponible. Verifica que esté encendido y conectado a la red.
          </div>
        )}

        {isLocked && (
          <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl p-3 text-sm text-amber-700 dark:text-amber-400">
            Demasiados intentos fallidos. Espera a que expire el bloqueo y verifica las credenciales.
          </div>
        )}

        {!isElectron ? (
          <div className="grid grid-cols-2 gap-2">
            {(['OPEN_DOOR', 'CLOSE_DOOR'] as RemoteOperation[]).map((op) => (
              <button
                key={op}
                onClick={() => sendRemoteCommand(op)}
                disabled={remoteBusy || !canControl}
                className={cn(
                  'flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed',
                  op === 'OPEN_DOOR'
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    : 'border border-border text-text-primary hover:bg-border/50'
                )}
              >
                <span className={cn('transition-transform duration-300', op === 'OPEN_DOOR' && doorSuccess && 'scale-125')}>
                  {op === 'OPEN_DOOR' ? <DoorOpen className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                </span>
                {remoteBusy ? '...' : op === 'OPEN_DOOR' ? 'Abrir' : 'Cerrar'}
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {DOOR_ACTIONS.map(({ cmd, label, icon, cls }) => (
              <button
                key={cmd}
                onClick={() => sendDoorCommand(cmd)}
                disabled={doorBusy || !canControl}
                className={cn(
                  'flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed',
                  cls
                )}
              >
                <span className={cn(
                  'transition-transform duration-300',
                  cmd === 'unlock' && doorSuccess && 'scale-125'
                )}>
                  {icon}
                </span>
                {label}
              </button>
            ))}
          </div>
        )}

      </div>

      {/* ── Technical info accordion ──────────────────────────────────────── */}
      <div className="border border-border rounded-2xl overflow-hidden">
        <button
          type="button"
          onClick={() => setTechOpen(!techOpen)}
          aria-expanded={techOpen}
          aria-controls="tech-panel"
          className="w-full flex items-center justify-between px-5 py-3.5 bg-surface hover:bg-border/30 transition-colors text-left"
        >
          <span className="text-sm font-medium text-text-primary">Información técnica</span>
          <ChevronDown
            className={cn('w-4 h-4 text-text-muted transition-transform duration-200', techOpen && 'rotate-180')}
          />
        </button>

        {techOpen && (
          <div id="tech-panel" className="px-5 py-4 border-t border-border flex flex-col gap-4">
            {/* Basic info */}
            <div className="flex flex-col gap-2">
              <TechRow label="IP / Host" value={device.host} />
              <TechRow label="Puerto" value={String(device.port)} />
              {device.last_seen_at && (
                <TechRow
                  label="Último contacto"
                  value={new Date(device.last_seen_at).toLocaleString('es-CO')}
                />
              )}
            </div>

            {/* Capabilities */}
            {capabilities && (
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-medium text-text-muted uppercase tracking-wide">Capacidades</p>
                <div className="grid grid-cols-1 gap-1">
                  {Object.entries(capabilities)
                    .filter(([k]) => k.startsWith('isSupport'))
                    .map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between gap-2 py-1">
                        <span className="text-sm text-text-muted">{capabilityLabel(k)}</span>
                        <span className={cn(
                          'text-xs font-medium px-2 py-0.5 rounded-full',
                          v
                            ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                            : 'bg-slate-100 dark:bg-slate-500/10 text-slate-400'
                        )}>
                          {v ? 'Disponible' : 'No disponible'}
                        </span>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}

            {!capabilities && isElectron && (
              <p className="text-text-muted text-xs">Cargando capacidades del dispositivo...</p>
            )}
            {!capabilities && !isElectron && (
              <p className="text-text-muted text-xs">Las capacidades del dispositivo requieren la app de escritorio.</p>
            )}
          </div>
        )}
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div role="tablist" className="flex border-b border-border">
        {(['events', 'users'] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            aria-controls={`panel-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab
                ? 'border-accent text-accent'
                : 'border-transparent text-text-muted hover:text-text-primary'
            )}
          >
            {tab === 'events' ? 'Eventos' : 'Usuarios'}
          </button>
        ))}
      </div>

      <div className="bg-surface border border-border rounded-2xl p-4">
        {activeTab === 'events' && (
          !isElectron ? (
            <p className="text-text-muted text-sm">Los eventos en tiempo real requieren la app de escritorio.</p>
          ) : (
            proxyUrl && proxyToken && id && (
              <DeviceEventsTab
                deviceId={id}
                proxyUrl={proxyUrl}
                proxyToken={proxyToken}
              />
            )
          )
        )}
        {activeTab === 'users' && id && (
          <DeviceUsersTab deviceId={id} />
        )}
      </div>

    </div>
  )
}

function TechRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className="text-sm text-text-muted">{label}</span>
      <span className="text-sm text-text-primary font-mono">{value}</span>
    </div>
  )
}
